/**
 * Session-expiration guard.
 *
 * The app talks to the API from many places — most services call the global
 * `fetch` directly, while a few (financial-data, instrument detail, position
 * form) use `axios`. Each attaches its own `Authorization: Bearer <token>`
 * header. When the JWT expires or is revoked, those calls return 401 and the
 * calling screen just shows an error.
 *
 * Instead, we want one global behavior: clear the session and send the user
 * back to the Login screen. `installAuthExpirationGuard()` wraps the global
 * `fetch` and registers an `axios` response interceptor so that any
 * *authenticated* request (one carrying an `Authorization` header) that comes
 * back 401 fires the registered unauthorized handler. Login/register requests
 * send no `Authorization` header, so a bad-credentials 401 on login correctly
 * does NOT trigger a redirect.
 *
 * `AuthContext` registers the handler that clears tokens and flips
 * `isAuthenticated` to false, which makes `AuthNavigator` render Login.
 */

import axios from 'axios';

type UnauthorizedHandler = () => void | Promise<void>;

let unauthorizedHandler: UnauthorizedHandler | null = null;
let notifying = false;
let cooldownTimer: ReturnType<typeof setTimeout> | null = null;
const NOTIFY_COOLDOWN_MS = 1500;
const FETCH_GUARD_TAG = '__authExpirationGuard';
let axiosInterceptorInstalled = false;

function clearCooldown(): void {
  if (cooldownTimer) {
    clearTimeout(cooldownTimer);
    cooldownTimer = null;
  }
}

/**
 * Register (or clear with `null`) the callback invoked when an authenticated
 * request expires. Re-registering also resets the dedupe flag (and any pending
 * cooldown) so a new session can expire again later.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
  notifying = false;
  clearCooldown();
}

/**
 * Invoke the handler at most once per burst of 401s. Concurrent expired
 * requests (e.g. a flurry of background refetches) collapse into a single
 * redirect instead of a storm of toasts/navigations.
 */
function triggerUnauthorized(): void {
  if (notifying || !unauthorizedHandler) return;
  notifying = true;
  try {
    const result = unauthorizedHandler() as unknown;
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      (result as Promise<unknown>).catch(() => undefined);
    }
  } finally {
    clearCooldown();
    cooldownTimer = setTimeout(() => {
      notifying = false;
      cooldownTimer = null;
    }, NOTIFY_COOLDOWN_MS);
  }
}

/**
 * True if the given header carrier (plain object, array of pairs, or a
 * `Headers`/`AxiosHeaders` instance exposing `.get`) includes an
 * `Authorization` entry.
 */
function headersCarrierHasAuth(headers: unknown): boolean {
  if (!headers) return false;

  if (typeof (headers as { get?: unknown }).get === 'function') {
    try {
      return !!(headers as { get: (k: string) => string | null }).get('Authorization');
    } catch {
      // fall through to other shapes
    }
  }

  if (Array.isArray(headers)) {
    return headers.some(([k]) => String(k ?? '').toLowerCase() === 'authorization');
  }

  if (typeof headers === 'object') {
    return Object.keys(headers as Record<string, unknown>).some(
      (k) => k.toLowerCase() === 'authorization'
    );
  }

  return false;
}

/**
 * True if a `fetch(input, init)` call carries an `Authorization` header —
 * either on `init.headers` or on a `Request` passed as `input`.
 */
export function requestHasAuthHeader(input: unknown, init?: { headers?: unknown } | null): boolean {
  if (init?.headers && headersCarrierHasAuth(init.headers)) return true;
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return headersCarrierHasAuth(input.headers);
  }
  return false;
}

/**
 * `axios` response-error handler. Rejects the error unchanged (so callers still
 * see it) but first fires the unauthorized handler for an authenticated 401.
 */
export async function handleAxiosError(error: unknown): Promise<never> {
  try {
    const status = (error as { response?: { status?: number } })?.response?.status;
    const headers = (error as { config?: { headers?: unknown } })?.config?.headers;
    if (status === 401 && headersCarrierHasAuth(headers)) {
      triggerUnauthorized();
    }
  } catch {
    // never let the guard break the request
  }
  return Promise.reject(error);
}

function installFetchGuard(): void {
  if (typeof globalThis === 'undefined' || typeof globalThis.fetch !== 'function') {
    return;
  }

  const current = globalThis.fetch as unknown as {
    (input: unknown, init?: unknown): Promise<Response>;
    __authExpirationGuard?: boolean;
    bind: (ctx: unknown) => typeof current;
  };

  if (!current || current[FETCH_GUARD_TAG]) return;

  const originalFetch = current.bind(globalThis);

  const wrapped = async (input: unknown, init?: unknown): Promise<Response> => {
    const response = await originalFetch(input, init);
    try {
      if (
        response &&
        response.status === 401 &&
        requestHasAuthHeader(input, init as { headers?: unknown } | undefined)
      ) {
        triggerUnauthorized();
      }
    } catch {
      // never let the guard break the request
    }
    return response;
  };

  (wrapped as unknown as Record<string, unknown>)[FETCH_GUARD_TAG] = true;
  globalThis.fetch = wrapped as typeof fetch;
}

function installAxiosInterceptor(): void {
  if (axiosInterceptorInstalled) return;
  try {
    axios.interceptors.response.use(
      (response) => response,
      (error: unknown) => handleAxiosError(error)
    );
    axiosInterceptorInstalled = true;
  } catch {
    // axios unavailable or misconfigured — fetch guard still covers fetch callers
  }
}

/**
 * Install the global session-expiration guard. Safe to call more than once:
 * the fetch wrapper tags itself and the axios interceptor is added only once.
 */
export function installAuthExpirationGuard(): void {
  installFetchGuard();
  installAxiosInterceptor();
}
